import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, CaretUpDown, Check, Plus, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { AppHeader } from "@/components/AppHeader";
import { AddressCombo } from "@/components/AddressCombo";
import { CUSTOMER_GROUPS } from "@/pages/Customers";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "",
  company: "",
  contact_type: "person",
  organization_id: "",
  phone: "",
  email: "",
  address: "",
  status: "Lead",
  group: "Regular",
  portal_prices: "default",
  referred_by: "",
  notes: "",
  contacts: [],
  job_addresses: [],
};

export default function CustomerForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    organization_id: searchParams.get("organization_id") || "",
  }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [refOpen, setRefOpen] = useState(false);

  useEffect(() => {
    api
      .get("/customers", { params: { fields: "basic", contact_type: "organization", limit: 2000 } })
      .then(({ data }) => setOrgs(data || []))
      .catch(() => setOrgs([]));
    api
      .get("/customers", { params: { fields: "basic", limit: 2000 } })
      .then(({ data }) => setAccounts(data || []))
      .catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/customers/${id}`).then(({ data }) =>
      setForm({
        ...EMPTY,
        ...data,
        contact_type: data.contact_type || (data.company ? "organization" : "person"),
      }),
    );
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedRef = accounts.find((a) => a.id === form.referred_by);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      name: form.name,
      company: form.company,
      contact_type: form.contact_type,
      approval_waived: !!form.approval_waived,
      organization_id: form.contact_type === "person" ? form.organization_id || "" : "",
      phone: form.phone,
      email: form.email,
      address: form.address,
      status: form.status,
      group: form.group,
      portal_prices: ["default", "on", "off"].includes(form.portal_prices)
        ? form.portal_prices
        : "default",
      referred_by: form.referred_by || "",
      notes: form.notes,
      contacts: (form.contacts || []).filter(
        (c) => c.name.trim() || c.phone.trim() || c.email.trim(),
      ),
      job_addresses: (form.job_addresses || []).filter((a) => a.trim()),
    };
    try {
      const { data } = id
        ? await api.put(`/customers/${id}`, payload)
        : await api.post("/customers", payload);
      toast.success(id ? "Account updated" : `${data.name} added`);
      navigate(`/customers/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Input
        data-testid={`customer-${key}-input`}
        value={form[key]}
        onChange={(e) => set(key)(e.target.value)}
        className="h-12 border-zinc-800 bg-zinc-900 text-base"
        {...props}
      />
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit account" : "New account"} />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Account" : "New Account"}
        </h1>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="customer-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Contact Type
              </Label>
              <div className="grid grid-cols-2 gap-2" data-testid="customer-contact-type">
                {[
                  ["organization", "Organization"],
                  ["person", "Person"],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`customer-type-${v}`}
                    onClick={() => set("contact_type")(v)}
                    className={`h-12 rounded-md border text-sm font-semibold transition-colors ${
                      form.contact_type === v
                        ? "border-indigo-500/60 bg-indigo-500/15 text-indigo-200"
                        : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {form.contact_type === "person" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Organization
                </Label>
                <Select
                  value={form.organization_id || "none"}
                  onValueChange={(v) => set("organization_id")(v === "none" ? "" : v)}
                >
                  <SelectTrigger
                    data-testid="customer-organization-select"
                    className="h-12 border-zinc-800 bg-zinc-900 text-base"
                  >
                    <SelectValue placeholder="No organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No organization</SelectItem>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.company || o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div
            data-testid="customer-approval-waived"
            className="flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-900 px-4 py-3"
          >
            <span>
              <span className="block text-sm font-semibold text-zinc-100">No approval or signature needed</span>
              <span className="block text-xs text-zinc-500">
                Quotes and drawings for this {form.contact_type === "organization" ? "organization and its people" : "account"} can be approved by staff without a customer signature.
              </span>
            </span>
            <Switch
              data-testid="customer-approval-waived-input"
              checked={!!form.approval_waived}
              onCheckedChange={(v) => set("approval_waived")(v)}
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {field(form.contact_type === "organization" ? "Primary Contact Name" : "Name", "name", {
              required: true,
              placeholder: "Maria Lopez",
            })}
            {field(form.contact_type === "organization" ? "Organization Name" : "Company (optional)", "company", { placeholder: "Lopez Builders" })}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {field("Phone", "phone", { placeholder: "(555) 201-8890" })}
            {field("Email", "email", {
              type: "email",
              placeholder: "maria@builders.com",
            })}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Billing Address
            </Label>
            <AddressCombo
              testId="customer-address"
              value={form.address}
              onChange={(v) => set("address")(v)}
              placeholder="142 Elm St, Springfield"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Status
              </Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger
                  data-testid="customer-status-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {["Lead", "Active", "Completed", "Inactive"].map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`customer-status-${s}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Group
              </Label>
              <Select value={form.group} onValueChange={set("group")}>
                <SelectTrigger
                  data-testid="customer-group-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {CUSTOMER_GROUPS.map((g) => (
                    <SelectItem
                      key={g}
                      value={g}
                      data-testid={`customer-group-${g}`}
                    >
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Portal inventory prices
              </Label>
              <Select
                value={form.portal_prices || "default"}
                onValueChange={(v) => set("portal_prices")(v)}
              >
                <SelectTrigger
                  data-testid="customer-portal-prices-select"
                  className="h-12 border-zinc-800 bg-zinc-900"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default" data-testid="portal-prices-default">
                    Follow global setting
                  </SelectItem>
                  <SelectItem value="on" data-testid="portal-prices-on">
                    Always show prices
                  </SelectItem>
                  <SelectItem value="off" data-testid="portal-prices-off">
                    Never show prices
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">
                {form.portal_prices === "on"
                  ? "This account sees prices even if global prices are off."
                  : form.portal_prices === "off"
                    ? "This account never sees prices."
                    : "Uses the shop's global price setting and this account's price group."}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Referred by
              </Label>
              <Popover open={refOpen} onOpenChange={setRefOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="customer-referred-by-trigger"
                    className="flex h-12 w-full items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 text-left text-base"
                  >
                    <span className={selectedRef ? "truncate" : "truncate text-zinc-500"}>
                      {selectedRef ? selectedRef.company || selectedRef.name : "No referral"}
                    </span>
                    <CaretUpDown size={16} className="shrink-0 text-zinc-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command>
                    <CommandInput placeholder="Search accounts…" data-testid="customer-referred-by-search" />
                    <CommandList>
                      <CommandEmpty>No accounts found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__ no referral"
                          data-testid="customer-referred-by-none"
                          onSelect={() => {
                            set("referred_by")("");
                            setRefOpen(false);
                          }}
                        >
                          <Check size={14} weight="bold" className={`mr-2 ${!form.referred_by ? "opacity-100" : "opacity-0"}`} />
                          No referral
                        </CommandItem>
                        {accounts
                          .filter((a) => a.id !== id)
                          .map((a) => (
                            <CommandItem
                              key={a.id}
                              value={`${a.company || ""} ${a.name || ""} ${a.id}`}
                              data-testid={`customer-referred-by-option-${a.id}`}
                              onSelect={() => {
                                set("referred_by")(a.id);
                                setRefOpen(false);
                              }}
                            >
                              <Check size={14} weight="bold" className={`mr-2 ${form.referred_by === a.id ? "opacity-100" : "opacity-0"}`} />
                              <span className="truncate">
                                {a.company || a.name}
                                {a.company && a.name ? ` · ${a.name}` : ""}
                              </span>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-zinc-500">
                The account that referred this customer — drives the Referrals report.
              </p>
            </div>
          </div>
          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="contacts-editor"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Contacts
              </div>
              <Button
                type="button"
                data-testid="add-contact-button"
                variant="outline"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    contacts: [
                      ...(f.contacts || []),
                      { name: "", role: "", phone: "", email: "" },
                    ],
                  }))
                }
                className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
              >
                <Plus size={14} weight="bold" className="mr-2" /> Add Contact
              </Button>
            </div>
            <div className="mt-3 space-y-3">
              {(form.contacts || []).length === 0 && (
                <p className="text-sm text-zinc-500">
                  No contacts yet — add the people you deal with.
                </p>
              )}
              {(form.contacts || []).map((ct, i) => (
                <div
                  key={i}
                  className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]"
                >
                  {["name", "role", "phone", "email"].map((k) => (
                    <Input
                      key={k}
                      data-testid={`contact-${i}-${k}`}
                      value={ct[k] || ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          contacts: f.contacts.map((x, j) =>
                            j === i ? { ...x, [k]: e.target.value } : x,
                          ),
                        }))
                      }
                      placeholder={
                        {
                          name: "Name",
                          role: "Role (site super, designer…)",
                          phone: "Phone",
                          email: "Email",
                        }[k]
                      }
                      className="h-12 border-zinc-800 bg-zinc-950"
                    />
                  ))}
                  <Button
                    type="button"
                    data-testid={`remove-contact-${i}`}
                    variant="outline"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        contacts: f.contacts.filter((_, j) => j !== i),
                      }))
                    }
                    className="h-12 border-zinc-700 bg-transparent px-3 text-red-400 hover:bg-zinc-800"
                  >
                    <X size={14} weight="bold" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="job-addresses-editor"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Job Addresses
              </div>
              <Button
                type="button"
                data-testid="add-job-address-button"
                variant="outline"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    job_addresses: [...(f.job_addresses || []), ""],
                  }))
                }
                className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
              >
                <Plus size={14} weight="bold" className="mr-2" /> Add Address
              </Button>
            </div>
            <div className="mt-3 space-y-3">
              {(form.job_addresses || []).length === 0 && (
                <p className="text-sm text-zinc-500">
                  No extra job addresses yet.
                </p>
              )}
              {(form.job_addresses || []).map((addr, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    data-testid={`job-address-${i}`}
                    value={addr}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        job_addresses: f.job_addresses.map((x, j) =>
                          j === i ? e.target.value : x,
                        ),
                      }))
                    }
                    placeholder="88 Oak Ave, Unit 2, Springfield"
                    className="h-12 border-zinc-700 bg-zinc-950"
                  />
                  <Button
                    type="button"
                    data-testid={`remove-job-address-${i}`}
                    variant="outline"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        job_addresses: f.job_addresses.filter(
                          (_, j) => j !== i,
                        ),
                      }))
                    }
                    className="h-12 border-zinc-700 bg-transparent px-3 text-red-400 hover:bg-zinc-800"
                  >
                    <X size={14} weight="bold" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Notes
            </Label>
            <Textarea
              data-testid="customer-notes-input"
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              rows={4}
              placeholder="Preferred contact time, budget range, design preferences…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          {error && (
            <p
              data-testid="customer-form-error"
              className="text-sm font-medium text-red-400"
            >
              {error}
            </p>
          )}

          <div className="no-print fixed bottom-0 left-0 right-0 border-t border-zinc-800 bg-zinc-950 p-3 sm:static sm:border-0 sm:bg-transparent sm:p-0">
            <Button
              data-testid="save-customer-button"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:w-auto sm:px-10"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Add Account"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
